import { useCallback } from 'react';
import { restoreScrollAfterAuth } from '@pkgs/components/utils/preserveScrollDuringAuth';
import { type ComponentTFunction, type TransComponent, type TFunction } from '@pkgs/components/providers/AppContextProvider';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';

type UseUserAccountStatusMessagesParams = {
  tFunc: TFunction;
  transComponent: TransComponent;
};

export const useUserAccountStatusMessages = ({ tFunc, transComponent: TransComponent }: UseUserAccountStatusMessagesParams) => {
  const { showMessage } = useStatusMessage();
  const typedTFunction = tFunc as ComponentTFunction;

  const onSignInSuccess = useCallback(() => {
    restoreScrollAfterAuth();
    showMessage(typedTFunction('userAccount.signInSuccess.message'), { alertType: alertVariants.success });
  }, [showMessage, typedTFunction]);

  const onEmailVerificationSuccess = useCallback(() => {
    showMessage(typedTFunction('userAccount.emailVerificationSuccess.message'), { alertType: alertVariants.success });
  }, [showMessage, typedTFunction]);

  const onResetPasswordSuccess = useCallback(() => {
    showMessage(typedTFunction('userAccount.resetPasswordSuccess.message'), { alertType: alertVariants.success });
  }, [showMessage, typedTFunction]);

  const onSignUpSuccess = useCallback(() => {
    restoreScrollAfterAuth();
    showMessage(
      <TransComponent
        t={tFunc}
        i18nKey="userAccount.signUpSuccess.message"
        components={{
          bold: <b />,
        }}
      />,
      { alertType: alertVariants.success },
    );
  }, [showMessage, TransComponent, tFunc]);

  return { onSignInSuccess, onEmailVerificationSuccess, onResetPasswordSuccess, onSignUpSuccess };
};
